import {Component, OnInit, Input, Inject, ViewChild} from '@angular/core';
import {UUID} from '@util/types';
import {Cycle} from '@services/model/cycle';
import {PrepareUpdateService} from '@services/prepare-update-service';
import {PreparedUpdateResource} from '@services/resource/prepared-update-resource';
import {MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle} from '@angular/material/dialog';
import {Sort, MatSort, SortDirection} from '@angular/material/sort';
import {CycleService} from '@services/cycle.service';
import {Observable, combineLatest} from 'rxjs';
import {startWith, map, share} from 'rxjs/operators';
import {ordString} from 'fp-ts/lib/Ord';
import {flip} from 'fp-ts/lib/function';
import {UntypedFormGroup, UntypedFormControl, ReactiveFormsModule} from '@angular/forms';
import {StatusUpdateCommentResource} from '@services/resource/status-update-comment-resource';
import {ProductStatus} from '@services/model/product';
import {StatusUpdateInfoResource} from '@services/resource/status-update-info-resource';
import {StatusUpdateMailResource} from '@services/resource/status-update-mail-resource';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatFormField} from '@angular/material/form-field';
import {MarkantElementsModule} from '@markant/elements-angular';
import {CommonModule} from '@angular/common';
import {MomentPipe} from '@util/pipes/moment-pipe';
import {ProductStateTranslate} from '@util/pipes/product-state-translate';
import {MatInput} from '@angular/material/input';

export interface ExecuteUpdateData {
  cycleId: UUID;
}

@Component({
  selector: 'app-execute-updates',
  templateUrl: './execute-updates.component.html',
  styleUrls: ['./execute-updates.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatTable,
    MatCheckbox,
    ReactiveFormsModule,
    MatFormField,
    MarkantElementsModule,
    MatCellDef,
    MatHeaderCellDef,
    MatRowDef,
    MatHeaderRowDef,
    MomentPipe,
    MatDialogTitle,
    MatDialogContent,
    MatSort,
    MatHeaderRow,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatRow,
    ProductStateTranslate,
    MatInput,
    MatDialogActions,
  ],
})
export class ExecuteUpdatesComponent implements OnInit {
  @Input() cycleId: UUID;

  @ViewChild(MatSort, {static: true}) matSort: MatSort;
  cycle?: Cycle;

  updates$: Observable<PreparedUpdateResource[]>;
  updatesContainInvite$: Observable<boolean>;
  updatesContainNotInvite$: Observable<boolean>;

  public readonly displayedColumns = ['product', 'startup', 'change', 'sendMail'];

  textForm = new UntypedFormGroup({
    invited: new UntypedFormControl(''),
    notInvited: new UntypedFormControl(''),
  });

  sendMailMap: Map<UUID, boolean> = new Map<UUID, boolean>();

  constructor(
    private readonly preparedUpdateService: PrepareUpdateService,
    private readonly cycleService: CycleService,
    @Inject(MAT_DIALOG_DATA) public data: ExecuteUpdateData,
    private readonly dialogRef: MatDialogRef<object>,
  ) {}

  ngOnInit() {
    if (this.data) {
      this.cycleId = this.data.cycleId;
    }

    this.cycleService.getById(this.cycleId).subscribe((c) => (this.cycle = c));

    /* prepared updates */
    const sortField$: Observable<Sort> = this.matSort.sortChange.pipe(startWith({active: 'newStatus', direction: 'asc' as SortDirection}));
    this.updates$ = combineLatest([this.preparedUpdateService.getPreparedUpdates(this.cycleId), sortField$]).pipe(
      map((x) => {
        const updates = x[0];
        const matSort = x[1];

        let comp: (a: PreparedUpdateResource, b: PreparedUpdateResource) => number =
          matSort.active === 'startup'
            ? (a, b) => ordString.compare(a.startupName, b.startupName)
            : matSort.active === 'newStatus'
              ? (a, b) => ordString.compare(a.newStatus, b.newStatus)
              : (a, b) => ordString.compare(a.productName, b.productName);

        if (matSort.direction === 'desc') {
          comp = flip(comp);
        }

        updates.forEach((update) => {
          this.sendMailMap.set(update.productId, update.newStatus !== ProductStatus.invited);
        });

        return updates.sort(comp);
      }),
      share(),
    );
    /* init the visibility toggles */
    this.updatesContainInvite$ = this.updates$.pipe(map((us) => us.findIndex((u) => u.newStatus !== ProductStatus.invited) >= 0));
    this.updatesContainNotInvite$ = this.updates$.pipe(map((us) => us.findIndex((u) => u.newStatus !== ProductStatus.notInvited) >= 0));
  }

  cancel(): void {
    this.dialogRef.close();
  }

  updateMailMap(productId: UUID, value: boolean): void {
    this.sendMailMap.set(productId, value);
  }

  execute(): void {
    const statusUpdateComments: StatusUpdateCommentResource = this.textForm.value;
    const statusUpdateMail: StatusUpdateMailResource = {
      sendMailMap: Object.fromEntries(this.sendMailMap),
    };
    const updateInfo: StatusUpdateInfoResource = {
      statusUpdateComments,
      statusUpdateMail,
    };
    this.preparedUpdateService.executePreparedUpdates(this.cycleId, updateInfo).subscribe(() => {
      this.dialogRef.close();
    });
  }
}
